/**
 * Centralized registry of all public-facing pages with default SEO metadata.
 * Used by SeoManager to sync missing pages to the page_seo table.
 */

export interface PageDefinition {
  path: string;
  title: string;
  description: string;
  priority?: number;
  changeFreq?: string;
  includeInSitemap?: boolean;
  noIndex?: boolean;
}

export const PAGE_REGISTRY: PageDefinition[] = [
  // Core pages
  {
    path: '/',
    title: 'Open Automotive Data Standards - Free JSON Schemas',
    description: 'Unified data standards for automotive API integration. Free, open-source JSON schemas for vehicles, customers, and service operations.',
    priority: 1.0,
    changeFreq: 'weekly',
  },
  {
    path: '/api-standards',
    title: 'API Standards - Vehicle, Customer & Service Schemas',
    description: 'Browse and explore automotive data schemas. Standardized JSON structures for DMS, CRM, and service integrations.',
    priority: 0.9,
    changeFreq: 'weekly',
  },
  {
    path: '/api-standards/builder',
    title: 'Schema Builder - Generate Code from Standards',
    description: 'Build custom schemas from the field library. Generate TypeScript, JSON Schema, and OpenAPI specs.',
    priority: 0.7,
    changeFreq: 'monthly',
  },
  {
    path: '/proposals',
    title: 'Community Proposals - Shape the Standards',
    description: 'Submit and vote on proposed changes to automotive data standards. Help shape the future of automotive APIs.',
    priority: 0.6,
    changeFreq: 'daily',
  },

  // ADF Standard pages
  {
    path: '/adf-standard',
    title: 'ADF Standard - Auto-lead Data Format Specification',
    description: 'Browse the ADF (Auto-lead Data Format) XML specification. Understand element structure, attributes, and field mappings for automotive lead data exchange.',
    priority: 0.8,
    changeFreq: 'weekly',
  },
  {
    path: '/adf-standard/builder',
    title: 'ADF Builder - Custom ADF Subset Generator',
    description: 'Create custom ADF subsets for your integration. Select elements, preview XML output, and export templates.',
    priority: 0.7,
    changeFreq: 'monthly',
  },

  // Documentation hub
  {
    path: '/docs',
    title: 'Documentation - Implementation Guides & Reference',
    description: 'Learn how to implement automotive data standards. Guides, API reference, and best practices.',
    priority: 0.8,
    changeFreq: 'weekly',
  },
  {
    path: '/docs/guides',
    title: 'Guides - Getting Started & Best Practices',
    description: 'Step-by-step guides for implementing automotive API standards. From basics to advanced patterns.',
    priority: 0.7,
    changeFreq: 'weekly',
  },
  {
    path: '/docs/api',
    title: 'API Reference - Endpoints & Authentication',
    description: 'Technical reference for the AutomotiveAPI standards API. Endpoints, authentication, and examples.',
    priority: 0.7,
    changeFreq: 'monthly',
  },
  {
    path: '/docs/data-types',
    title: 'Data Types - Formats, Patterns & Validation',
    description: 'Reference for data types used across automotive schemas. VINs, phone numbers, dates, and more.',
    priority: 0.6,
    changeFreq: 'monthly',
  },
  {
    path: '/docs/field-library',
    title: 'Field Library - Reusable Field Definitions',
    description: 'Browse the library of reusable fields for automotive data. Standardized naming and validation.',
    priority: 0.6,
    changeFreq: 'monthly',
  },
  {
    path: '/docs/lookups',
    title: 'Lookup Tables - Standardized Values & Enums',
    description: 'Reference tables for standardized automotive values. Vehicle types, service categories, and more.',
    priority: 0.6,
    changeFreq: 'monthly',
  },

  // Static pages
  {
    path: '/about',
    title: 'About - Our Mission to Standardize Automotive Data',
    description: 'Learn about the mission and community behind AutomotiveAPI. Building open standards for automotive integration.',
    priority: 0.5,
    changeFreq: 'monthly',
  },
  {
    path: '/use-cases',
    title: 'Use Cases - DMS, CRM & Service Integration',
    description: 'Real-world applications of automotive data standards. See how businesses use standardized APIs.',
    priority: 0.6,
    changeFreq: 'monthly',
  },
  {
    path: '/partners',
    title: 'Partners - Organizations Building Standards',
    description: 'Organizations contributing to automotive data standards. Join the community of innovators.',
    priority: 0.5,
    changeFreq: 'monthly',
  },
  {
    path: '/contact',
    title: 'Contact - Support & Partnership Inquiries',
    description: 'Get in touch for support, partnership opportunities, or to contribute to automotive standards.',
    priority: 0.4,
    changeFreq: 'monthly',
  },
];

// Excluded from registry (not public or admin-only):
// - /auth (no-index, authentication page)
// - /dashboard (admin only)
// - /account (admin only)
// - /docs/guides/:slug (dynamic - SEO handled by article metadata)
